/**
 * AgentList Component
 *
 * Admin component for managing agents with:
 * - Agent list display with filtering and sorting
 * - Status badges (slot_reserved, active, disabled, terminated)
 * - Operational status badges (idle, working, focus, in_call, offline)
 * - Enable/disable actions
 * - Session info display
 * - Activate agent action
 *
 * @module components/admin/AgentList
 */

import { useState, useCallback, useMemo, memo, useEffect } from 'react';
import {
  Bot,
  Server,
  Circle,
  Power,
  PowerOff,
  Play,
  PlayCircle,
  StopCircle,
  Trash2,
  Settings,
  RefreshCw,
  Search,
  ChevronDown,
  ChevronUp,
  Clock,
  Cpu,
  Activity,
  AlertCircle,
  Loader2,
  FolderOpen,
  Share2,
  VolumeX,
  Volume2,
  BellOff,
  Bell,
  Sparkles,
  Shield
} from 'lucide-react';
import { Avatar } from '../common/Avatar.jsx';

// ============================================
// Constants
// ============================================

/**
 * Agent lifecycle status configuration
 */
const AGENT_STATUS = {
  SLOT_RESERVED: 'slot_reserved',
  PROVISIONING: 'provisioning',
  ACTIVE: 'active',
  DISABLED: 'disabled',
  TERMINATED: 'terminated'
};

/**
 * Agent operational status configuration
 */
const OPERATIONAL_STATUS = {
  READY: 'ready',
  WORKING: 'working',
  FOCUS: 'focus',
  IN_CALL: 'in_call',
  OFFLINE: 'offline'
};

/**
 * Agent chat mode configuration
 */
const CHAT_MODE = {
  NORMAL: 'NORMAL',
  MUTED: 'MUTED',
  DND: 'DND'
};

/**
 * Status badge colors for agent lifecycle status
 */
const STATUS_COLORS = {
  [AGENT_STATUS.SLOT_RESERVED]: {
    bg: 'bg-amber-500/20',
    text: 'text-amber-400',
    border: 'border-amber-500/30',
    dot: 'bg-amber-500'
  },
  [AGENT_STATUS.PROVISIONING]: {
    bg: 'bg-blue-500/20',
    text: 'text-blue-400',
    border: 'border-blue-500/30',
    dot: 'bg-blue-500 animate-pulse'
  },
  [AGENT_STATUS.ACTIVE]: {
    bg: 'bg-green-500/20',
    text: 'text-green-400',
    border: 'border-green-500/30',
    dot: 'bg-green-500'
  },
  [AGENT_STATUS.DISABLED]: {
    bg: 'bg-gray-500/20',
    text: 'text-gray-400',
    border: 'border-gray-500/30',
    dot: 'bg-gray-500'
  },
  [AGENT_STATUS.TERMINATED]: {
    bg: 'bg-red-500/20',
    text: 'text-red-400',
    border: 'border-red-500/30',
    dot: 'bg-red-500'
  }
};

/**
 * Status labels for agent lifecycle status
 */
const STATUS_LABELS = {
  [AGENT_STATUS.SLOT_RESERVED]: 'Slot Reserved',
  [AGENT_STATUS.PROVISIONING]: 'Provisioning',
  [AGENT_STATUS.ACTIVE]: 'Active',
  [AGENT_STATUS.DISABLED]: 'Disabled',
  [AGENT_STATUS.TERMINATED]: 'Terminated'
};

/**
 * Operational status colors
 */
const OPERATIONAL_STATUS_COLORS = {
  [OPERATIONAL_STATUS.READY]: {
    bg: 'bg-green-500/20',
    text: 'text-green-400',
    border: 'border-green-500/30',
    dot: 'bg-green-500'
  },
  [OPERATIONAL_STATUS.WORKING]: {
    bg: 'bg-yellow-500/20',
    text: 'text-yellow-400',
    border: 'border-yellow-500/30',
    dot: 'bg-yellow-500 animate-pulse'
  },
  [OPERATIONAL_STATUS.FOCUS]: {
    bg: 'bg-red-500/20',
    text: 'text-red-400',
    border: 'border-red-500/30',
    dot: 'bg-red-500'
  },
  [OPERATIONAL_STATUS.IN_CALL]: {
    bg: 'bg-purple-500/20',
    text: 'text-purple-400',
    border: 'border-purple-500/30',
    dot: 'bg-purple-500 animate-pulse'
  },
  [OPERATIONAL_STATUS.OFFLINE]: {
    bg: 'bg-gray-500/20',
    text: 'text-gray-400',
    border: 'border-gray-500/30',
    dot: 'bg-gray-500'
  }
};

/**
 * Operational status labels
 */
const OPERATIONAL_STATUS_LABELS = {
  [OPERATIONAL_STATUS.READY]: 'Ready',
  [OPERATIONAL_STATUS.WORKING]: 'Working',
  [OPERATIONAL_STATUS.FOCUS]: 'Focus Mode',
  [OPERATIONAL_STATUS.IN_CALL]: 'In Call',
  [OPERATIONAL_STATUS.OFFLINE]: 'Offline'
};

/**
 * Filter options for agent status
 */
const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All Agents' },
  { value: AGENT_STATUS.ACTIVE, label: 'Active' },
  { value: AGENT_STATUS.PROVISIONING, label: 'Provisioning' },
  { value: AGENT_STATUS.SLOT_RESERVED, label: 'Slot Reserved' },
  { value: AGENT_STATUS.DISABLED, label: 'Disabled' },
  { value: AGENT_STATUS.TERMINATED, label: 'Terminated' }
];

/**
 * Sort options for agent list
 */
const SORT_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'status', label: 'Status' },
  { value: 'host', label: 'Host' },
  { value: 'createdAt', label: 'Created' },
  { value: 'lastActive', label: 'Last Active' }
];

// ============================================
// Helper Functions
// ============================================

/**
 * Format date for display
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date string
 */
function formatDate(date) {
  if (!date) return 'Never';
  const d = new Date(date);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
  });
}

/**
 * Format session duration
 * @param {string|Date} startTime - Session start time
 * @returns {string} Duration string
 */
function formatDuration(startTime) {
  if (!startTime) return '-';
  const start = new Date(startTime);
  const now = new Date();
  const diffMs = now.getTime() - start.getTime();
  const hours = Math.floor(diffMs / 3600000);
  const mins = Math.floor((diffMs % 3600000) / 60000);

  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
}

// ============================================
// Sub-Components
// ============================================

/**
 * Agent avatar wrapper for admin list
 * Uses shared Avatar component with lifecycle status dot overlay
 *
 * @param {object} props - Component props
 * @param {string} props.name - Agent name
 * @param {string} [props.avatarUrl] - Avatar image URL
 * @param {string} [props.status] - Agent lifecycle status (for dot color when not active)
 * @param {string} [props.operationalStatus] - Operational status (for dot color when active)
 * @param {'sm'|'md'|'lg'} [props.size='md'] - Avatar size
 * @returns {React.ReactElement} Avatar element
 */
const AgentAvatar = memo(function AgentAvatar({
  name,
  avatarUrl,
  status,
  operationalStatus,
  size = 'md'
}) {
  const statusDotSizes = {
    sm: 'w-2.5 h-2.5 -bottom-0.5 -right-0.5',
    md: 'w-3 h-3 -bottom-0.5 -right-0.5',
    lg: 'w-3.5 h-3.5 -bottom-1 -right-1'
  };

  // Map operational status to Avatar status prop (for active agents)
  // For non-active agents, show lifecycle status dot
  const avatarStatus = operationalStatus ? operationalStatus : null;
  const dotColor = operationalStatus
    ? OPERATIONAL_STATUS_COLORS[operationalStatus]?.dot || 'bg-gray-500'
    : STATUS_COLORS[status]?.dot || 'bg-gray-500';

  // If agent has operationalStatus, use Avatar's built-in status indicator
  if (operationalStatus) {
    return (
      <Avatar
        name={name}
        avatarUrl={avatarUrl}
        type="agent"
        size={size}
        status={operationalStatus}
        className="border-2 border-gray-700 rounded-full"
      />
    );
  }

  // For non-active agents, show lifecycle status dot (slot_reserved, disabled, terminated)
  return (
    <div className="relative flex-shrink-0">
      <Avatar
        name={name}
        avatarUrl={avatarUrl}
        type="agent"
        size={size}
        showTooltip={false}
      />
      {/* Lifecycle status dot */}
      <div
        className={`
          absolute ${statusDotSizes[size]}
          ${dotColor}
          rounded-full border-2 border-gray-900
        `}
        title={STATUS_LABELS[status]}
      />
    </div>
  );
});

/**
 * Status badge component for agent lifecycle status
 * @param {object} props - Component props
 * @param {string} props.status - Agent status
 * @returns {React.ReactElement} Status badge
 */
const StatusBadge = memo(function StatusBadge({ status }) {
  const colors = STATUS_COLORS[status] || STATUS_COLORS[AGENT_STATUS.DISABLED];
  const label = STATUS_LABELS[status] || 'Unknown';

  return (
    <span
      className={`
        inline-flex items-center gap-1.5 px-2 py-0.5
        text-xs font-medium rounded-full
        ${colors.bg} ${colors.text} border ${colors.border}
      `}
    >
      <Circle className={`w-1.5 h-1.5 ${colors.dot} fill-current`} />
      {label}
    </span>
  );
});

/**
 * Operational status badge component
 * @param {object} props - Component props
 * @param {string} props.status - Operational status
 * @param {string} [props.project] - Current project name
 * @returns {React.ReactElement} Operational status badge
 */
const OperationalStatusBadge = memo(function OperationalStatusBadge({ status, project }) {
  const colors = OPERATIONAL_STATUS_COLORS[status] || OPERATIONAL_STATUS_COLORS[OPERATIONAL_STATUS.OFFLINE];
  const label = OPERATIONAL_STATUS_LABELS[status] || 'Unknown';

  return (
    <span
      className={`
        inline-flex items-center gap-1.5 px-2 py-0.5
        text-xs font-medium rounded-full
        ${colors.bg} ${colors.text} border ${colors.border}
      `}
      title={project ? `${label} on ${project}` : label}
    >
      <Circle className={`w-1.5 h-1.5 ${colors.dot} fill-current`} />
      {label}
      {project && (
        <span className="text-gray-500 truncate max-w-[80px]">
          &middot; {project}
        </span>
      )}
    </span>
  );
});

/**
 * Session info component
 * @param {object} props - Component props
 * @param {object} props.session - Session object
 * @returns {React.ReactElement|null} Session info or null
 */
const SessionInfo = memo(function SessionInfo({ session }) {
  if (!session) {
    return (
      <span className="text-xs text-gray-500 italic">No active session</span>
    );
  }

  return (
    <div className="flex items-center gap-3 text-xs text-gray-400">
      <span className="flex items-center gap-1" title="Session duration">
        <Clock className="w-3.5 h-3.5" />
        {formatDuration(session.startedAt)}
      </span>
      {session.pid && (
        <span className="flex items-center gap-1" title="Process ID">
          <Cpu className="w-3.5 h-3.5" />
          PID {session.pid}
        </span>
      )}
      {session.hasActiveClaudeSession && (
        <span className="flex items-center gap-1" title="Claude session">
          <Activity className="w-3.5 h-3.5 text-green-500" />
        </span>
      )}
    </div>
  );
});

/**
 * Action button component
 * @param {object} props - Component props
 * @param {Function} props.onClick - Click handler
 * @param {React.ComponentType} props.icon - Icon component
 * @param {string} props.label - Button label
 * @param {string} [props.variant='default'] - Button variant
 * @param {boolean} [props.disabled] - Whether button is disabled
 * @param {boolean} [props.loading] - Whether action is in progress
 * @returns {React.ReactElement} Action button
 */
const ActionButton = memo(function ActionButton({
  onClick,
  icon: Icon,
  label,
  variant = 'default',
  disabled = false,
  loading = false
}) {
  const variantClasses = {
    default: 'text-gray-400 hover:text-white hover:bg-gray-700',
    success: 'text-green-400 hover:text-green-300 hover:bg-green-500/20',
    danger: 'text-red-400 hover:text-red-300 hover:bg-red-500/20',
    warning: 'text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/20'
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      className={`
        p-1.5 rounded transition-colors
        ${variantClasses[variant]}
        disabled:opacity-50 disabled:cursor-not-allowed
      `}
      title={label}
    >
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <Icon className="w-4 h-4" />
      )}
    </button>
  );
});

/**
 * Search input component
 * @param {object} props - Component props
 * @param {string} props.value - Search value
 * @param {Function} props.onChange - Change handler
 * @param {string} [props.placeholder] - Placeholder text
 * @returns {React.ReactElement} Search input
 */
const SearchInput = memo(function SearchInput({
  value,
  onChange,
  placeholder = 'Search agents...'
}) {
  return (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="
          w-full pl-9 pr-3 py-2 rounded-md
          bg-gray-900 border border-gray-700 text-gray-100
          placeholder-gray-500
          focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent
          transition-colors
        "
      />
    </div>
  );
});

/**
 * Filter dropdown component
 * @param {object} props - Component props
 * @param {string} props.value - Selected value
 * @param {Function} props.onChange - Change handler
 * @param {Array} props.options - Filter options
 * @param {string} [props.label] - Dropdown label
 * @returns {React.ReactElement} Filter dropdown
 */
const FilterDropdown = memo(function FilterDropdown({
  value,
  onChange,
  options,
  label = 'Filter'
}) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="
          appearance-none pl-3 pr-8 py-2 rounded-md
          bg-gray-900 border border-gray-700 text-gray-100
          focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent
          transition-colors cursor-pointer
        "
        aria-label={label}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
    </div>
  );
});

/**
 * Empty state component
 * @param {object} props - Component props
 * @param {string} [props.message] - Empty state message
 * @param {string} [props.description] - Description text
 * @returns {React.ReactElement} Empty state
 */
const EmptyState = memo(function EmptyState({
  message = 'No agents found',
  description = 'Create an agent slot to get started.'
}) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="p-3 rounded-full bg-gray-800 mb-4">
        <Bot className="w-8 h-8 text-gray-500" />
      </div>
      <h3 className="text-lg font-medium text-gray-300 mb-1">{message}</h3>
      <p className="text-sm text-gray-500">{description}</p>
    </div>
  );
});

/**
 * Loading skeleton component
 * @returns {React.ReactElement} Loading skeleton
 */
const LoadingSkeleton = memo(function LoadingSkeleton() {
  return (
    <div className="space-y-3">
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className="flex items-center gap-4 p-4 rounded-lg bg-gray-800/50 animate-pulse"
        >
          <div className="w-10 h-10 rounded-full bg-gray-700" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-32 bg-gray-700 rounded" />
            <div className="h-3 w-48 bg-gray-700 rounded" />
          </div>
          <div className="h-6 w-20 bg-gray-700 rounded-full" />
        </div>
      ))}
    </div>
  );
});

/**
 * Error state component
 * @param {object} props - Component props
 * @param {string} props.message - Error message
 * @param {Function} [props.onRetry] - Retry handler
 * @returns {React.ReactElement} Error state
 */
const ErrorState = memo(function ErrorState({ message, onRetry }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="p-3 rounded-full bg-red-500/10 mb-4">
        <AlertCircle className="w-8 h-8 text-red-400" />
      </div>
      <h3 className="text-lg font-medium text-gray-300 mb-1">
        Failed to load agents
      </h3>
      <p className="text-sm text-red-400 mb-4">{message}</p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="
            flex items-center gap-2 px-4 py-2 rounded-md
            text-gray-300 bg-gray-700 hover:bg-gray-600
            transition-colors
          "
        >
          <RefreshCw className="w-4 h-4" />
          Retry
        </button>
      )}
    </div>
  );
});

/**
 * Single agent row component
 * @param {object} props - Component props
 * @param {object} props.agent - Agent object
 * @param {Function} [props.onEnable] - Enable handler
 * @param {Function} [props.onDisable] - Disable handler
 * @param {Function} [props.onActivate] - Activate handler
 * @param {Function} [props.onTerminate] - Terminate handler
 * @param {Function} [props.onDelete] - Delete handler
 * @param {Function} [props.onSettings] - Settings handler
 * @param {Function} [props.onStartSession] - Start session handler
 * @param {Function} [props.onStopSession] - Stop session handler
 * @param {Function} [props.onShare] - Share handler
 * @param {Function} [props.onToggleEditGrant] - Toggle CLAUDE.md edit grant handler
 * @param {number} [props.shareCount] - Number of users this agent is shared with
 * @param {boolean} [props.isActionLoading] - Whether an action is in progress
 * @param {string} [props.currentUserId] - Current user ID for ownership checks
 * @returns {React.ReactElement} Agent row
 */
const AgentRow = memo(function AgentRow({
  agent,
  onEnable,
  onDisable,
  onActivate,
  onTerminate,
  onDelete,
  onSettings,
  onStartSession,
  onStopSession,
  onShare,
  onToggleEditGrant,
  onChatModeChange,
  shareCount = 0,
  isActionLoading = false,
  currentUserId
}) {
  const {
    id,
    name,
    displayName,
    status,
    operationalStatus,
    currentProject,
    host,
    workingDirectory,
    avatarUrl,
    session,
    createdAt,
    activatedAt,
    updatedAt,
    uuid,
    ownerId,
    chatMode = CHAT_MODE.NORMAL,
    whisperEnabled = true,
    grants = []
  } = agent;

  const hasEditGrant = grants.includes('claude-md-edit');

  const canEnable = status === AGENT_STATUS.DISABLED;
  const canDisable = status === AGENT_STATUS.ACTIVE;
  const canActivate = status === AGENT_STATUS.SLOT_RESERVED;
  const canDelete = status === AGENT_STATUS.SLOT_RESERVED || status === AGENT_STATUS.TERMINATED;
  const hasActiveSession = session && session.status === 'running';
  const canStartSession = status === AGENT_STATUS.ACTIVE && !hasActiveSession;
  const canStopSession = status === AGENT_STATUS.ACTIVE && hasActiveSession;
  // Only agent owner can share
  const isOwner = currentUserId && ownerId && currentUserId === ownerId;
  // Check if user can modify chat mode (owner, lead, or admin can modify)
  const canModifyChatMode = status === AGENT_STATUS.ACTIVE && isOwner;
  const isMuted = chatMode === CHAT_MODE.MUTED;
  const isDnd = chatMode === CHAT_MODE.DND;

  return (
    <div
      className="
        flex items-start gap-4 p-4 rounded-lg
        bg-gray-800/50 border border-gray-700/50
        hover:bg-gray-800 hover:border-gray-700
        transition-colors
      "
    >
      {/* Avatar */}
      <AgentAvatar
        name={displayName || name}
        avatarUrl={avatarUrl ? `${avatarUrl}?t=${new Date(updatedAt).getTime()}` : null}
        status={status}
        operationalStatus={status === AGENT_STATUS.ACTIVE ? operationalStatus : undefined}
        size="md"
      />

      {/* Main content */}
      <div className="flex-1 min-w-0">
        {/* Header row */}
        <div className="flex items-center gap-2 mb-1">
          <h3 className="text-base font-semibold text-white truncate">
            {displayName || name}
          </h3>
          <span className="text-sm text-gray-500">@{name}</span>
        </div>

        {/* Status badges */}
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <StatusBadge status={status} />
          {status === AGENT_STATUS.ACTIVE && operationalStatus && (
            <OperationalStatusBadge
              status={operationalStatus}
              project={currentProject}
            />
          )}
          {/* Shared indicator */}
          {shareCount > 0 && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-purple-500/20 text-purple-400 border border-purple-500/30"
              title={`Shared with ${shareCount} user${shareCount !== 1 ? 's' : ''}`}
            >
              <Share2 className="w-3 h-3" />
              {shareCount}
            </span>
          )}
          {/* Chat mode indicator */}
          {isMuted && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30"
              title="Agent is muted - cannot send messages"
            >
              <VolumeX className="w-3 h-3" />
              Muted
            </span>
          )}
          {isDnd && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-red-500/20 text-red-400 border border-red-500/30"
              title="Agent is in Do Not Disturb mode - isolated from all channel activity"
            >
              <BellOff className="w-3 h-3" />
              DND
            </span>
          )}
          {/* Whisper mode indicator */}
          {status === AGENT_STATUS.ACTIVE && (
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${
                whisperEnabled
                  ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                  : 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
              }`}
              title={whisperEnabled ? 'Whisper mode enabled - receives automatic reminders' : 'Whisper mode disabled'}
            >
              <Sparkles className="w-3 h-3" />
              {whisperEnabled ? 'Whisper' : 'No Whisper'}
            </span>
          )}
          {/* Edit grant indicator */}
          {status === AGENT_STATUS.ACTIVE && hasEditGrant && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
              title="CLAUDE.md edit permission granted"
            >
              <Shield className="w-3 h-3" />
              Edit
            </span>
          )}
        </div>

        {/* Info row */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-400">
          {host && (
            <span className="flex items-center gap-1" title={`Host: ${host.displayName || host.name}`}>
              <Server className="w-3.5 h-3.5" />
              {host.displayName || host.name}
            </span>
          )}
          {workingDirectory && (
            <span className="flex items-center gap-1 truncate max-w-[200px]" title={workingDirectory}>
              <FolderOpen className="w-3.5 h-3.5" />
              {workingDirectory}
            </span>
          )}
          {createdAt && (
            <span title={`Created: ${new Date(createdAt).toLocaleString()}`}>
              Created {formatDate(createdAt)}
            </span>
          )}
        </div>

        {/* Session info */}
        {status === AGENT_STATUS.ACTIVE && (
          <div className="mt-2">
            <SessionInfo session={session} />
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1">
        {canActivate && onActivate && (
          <ActionButton
            onClick={() => onActivate(agent)}
            icon={Play}
            label="Activate agent"
            variant="success"
            loading={isActionLoading}
          />
        )}
        {canEnable && onEnable && (
          <ActionButton
            onClick={() => onEnable(agent)}
            icon={Power}
            label="Enable agent"
            variant="success"
            loading={isActionLoading}
          />
        )}
        {canDisable && onDisable && (
          <ActionButton
            onClick={() => onDisable(agent)}
            icon={PowerOff}
            label="Disable agent"
            variant="warning"
            loading={isActionLoading}
          />
        )}
        {canStartSession && onStartSession && (
          <ActionButton
            onClick={() => onStartSession(agent)}
            icon={PlayCircle}
            label="Start session"
            variant="success"
            loading={isActionLoading}
          />
        )}
        {canStopSession && onStopSession && (
          <ActionButton
            onClick={() => onStopSession(agent)}
            icon={StopCircle}
            label="Stop session"
            variant="warning"
            loading={isActionLoading}
          />
        )}
        {onSettings && (
          <ActionButton
            onClick={() => onSettings(agent)}
            icon={Settings}
            label="Agent settings"
          />
        )}
        {onToggleEditGrant && status === AGENT_STATUS.ACTIVE && isOwner && (
          <ActionButton
            onClick={() => onToggleEditGrant(agent)}
            icon={Shield}
            label={hasEditGrant ? 'Revoke edit permission' : 'Grant edit permission'}
            variant={hasEditGrant ? 'default' : 'warning'}
            loading={isActionLoading}
          />
        )}
        {onShare && status === AGENT_STATUS.ACTIVE && isOwner && (
          <ActionButton
            onClick={() => onShare(agent)}
            icon={Share2}
            label="Share agent"
          />
        )}
        {canModifyChatMode && onChatModeChange && (
          <>
            <ActionButton
              onClick={() => onChatModeChange(agent, isMuted ? CHAT_MODE.NORMAL : CHAT_MODE.MUTED)}
              icon={isMuted ? VolumeX : Volume2}
              label={isMuted ? 'Unmute agent' : 'Mute agent'}
              variant={isMuted ? 'warning' : 'default'}
            />
            <ActionButton
              onClick={() => onChatModeChange(agent, isDnd ? CHAT_MODE.NORMAL : CHAT_MODE.DND)}
              icon={isDnd ? BellOff : Bell}
              label={isDnd ? 'Enable notifications' : 'Do Not Disturb'}
              variant={isDnd ? 'danger' : 'default'}
            />
          </>
        )}
        {canDelete && onDelete && (
          <ActionButton
            onClick={() => onDelete(agent)}
            icon={Trash2}
            label="Delete agent"
            variant="danger"
            loading={isActionLoading}
          />
        )}
      </div>
    </div>
  );
});

// ============================================
// Main Component
// ============================================

/**
 * AgentList Component
 *
 * Admin component for viewing and managing agents.
 *
 * @param {object} props - Component props
 * @param {Array} [props.agents] - Array of agent objects
 * @param {boolean} [props.loading] - Whether agents are loading
 * @param {string} [props.error] - Error message
 * @param {Function} [props.onRefresh] - Refresh handler
 * @param {Function} [props.onEnable] - Enable agent handler (receives agent)
 * @param {Function} [props.onDisable] - Disable agent handler (receives agent)
 * @param {Function} [props.onActivate] - Activate agent handler (receives agent)
 * @param {Function} [props.onTerminate] - Terminate agent handler (receives agent)
 * @param {Function} [props.onDelete] - Delete agent handler (receives agent)
 * @param {Function} [props.onSettings] - Settings handler (receives agent)
 * @param {Function} [props.onStartSession] - Start session handler (receives agent)
 * @param {Function} [props.onStopSession] - Stop session handler (receives agent)
 * @param {Function} [props.onShare] - Share handler (receives agent)
 * @param {Function} [props.onToggleEditGrant] - Toggle CLAUDE.md edit grant handler (receives agent)
 * @param {Function} [props.onChatModeChange] - Chat mode change handler (receives agent, newMode)
 * @param {Object} [props.agentShareCounts] - Map of agent ID to share count
 * @param {Function} [props.onCreate] - Create new agent handler
 * @param {string} [props.actionLoadingId] - ID of agent with action in progress
 * @param {string} [props.currentUserId] - Current user ID for ownership checks
 * @param {string} [props.className] - Additional CSS classes
 * @returns {React.ReactElement} Agent list component
 */
export default function AgentList({
  agents = [],
  loading = false,
  error = null,
  onRefresh,
  onEnable,
  onDisable,
  onActivate,
  onTerminate,
  onDelete,
  onSettings,
  onStartSession,
  onStopSession,
  onShare,
  onToggleEditGrant,
  onChatModeChange,
  agentShareCounts = {},
  onCreate,
  actionLoadingId,
  currentUserId,
  className = ''
}) {
  // ----------------------------------------
  // State
  // ----------------------------------------

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  // ----------------------------------------
  // Filtered and Sorted Agents
  // ----------------------------------------

  const filteredAgents = useMemo(() => {
    let result = [...agents];

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (agent) =>
          agent.name?.toLowerCase().includes(query) ||
          agent.displayName?.toLowerCase().includes(query) ||
          agent.host?.name?.toLowerCase().includes(query) ||
          agent.host?.displayName?.toLowerCase().includes(query)
      );
    }

    // Filter by status
    if (statusFilter !== 'all') {
      result = result.filter((agent) => agent.status === statusFilter);
    }

    // Sort
    result.sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'name':
          comparison = (a.displayName || a.name || '').localeCompare(
            b.displayName || b.name || ''
          );
          break;
        case 'status':
          comparison = (a.status || '').localeCompare(b.status || '');
          break;
        case 'host':
          comparison = (a.host?.name || '').localeCompare(b.host?.name || '');
          break;
        case 'createdAt':
          comparison = new Date(a.createdAt || 0).getTime() -
            new Date(b.createdAt || 0).getTime();
          break;
        case 'lastActive':
          comparison = new Date(a.session?.lastActivityAt || 0).getTime() -
            new Date(b.session?.lastActivityAt || 0).getTime();
          break;
        default:
          comparison = 0;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [agents, searchQuery, statusFilter, sortBy, sortOrder]);

  // ----------------------------------------
  // Handlers
  // ----------------------------------------

  const toggleSortOrder = useCallback(() => {
    setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  }, []);

  // ----------------------------------------
  // Render
  // ----------------------------------------

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-indigo-500/10">
            <Bot className="w-6 h-6 text-indigo-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white">Agents</h2>
            <p className="text-sm text-gray-400">
              {filteredAgents.length} of {agents.length} agents
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {onRefresh && (
            <button
              type="button"
              onClick={onRefresh}
              disabled={loading}
              className="
                p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-700
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-colors
              "
              title="Refresh agents"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          )}
          {onCreate && (
            <button
              type="button"
              onClick={onCreate}
              className="
                flex items-center gap-2 px-4 py-2 rounded-md
                text-white bg-indigo-600 hover:bg-indigo-500
                transition-colors
              "
            >
              <Bot className="w-4 h-4" />
              New Agent
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[200px] max-w-md">
          <SearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search by name or host..."
          />
        </div>

        <FilterDropdown
          value={statusFilter}
          onChange={setStatusFilter}
          options={STATUS_FILTER_OPTIONS}
          label="Filter by status"
        />

        <FilterDropdown
          value={sortBy}
          onChange={setSortBy}
          options={SORT_OPTIONS}
          label="Sort by"
        />

        <button
          type="button"
          onClick={toggleSortOrder}
          className="
            p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-700
            transition-colors
          "
          title={`Sort ${sortOrder === 'asc' ? 'descending' : 'ascending'}`}
        >
          {sortOrder === 'asc' ? (
            <ChevronUp className="w-5 h-5" />
          ) : (
            <ChevronDown className="w-5 h-5" />
          )}
        </button>
      </div>

      {/* Content */}
      {loading && agents.length === 0 ? (
        <LoadingSkeleton />
      ) : error ? (
        <ErrorState message={error} onRetry={onRefresh} />
      ) : filteredAgents.length === 0 ? (
        <EmptyState
          message={searchQuery || statusFilter !== 'all'
            ? 'No agents match your filters'
            : 'No agents found'
          }
          description={searchQuery || statusFilter !== 'all'
            ? 'Try adjusting your search or filter criteria.'
            : 'Create an agent slot to get started.'
          }
        />
      ) : (
        <div className="space-y-3">
          {filteredAgents.map((agent) => (
            <AgentRow
              key={agent.id}
              agent={agent}
              onEnable={onEnable}
              onDisable={onDisable}
              onActivate={onActivate}
              onTerminate={onTerminate}
              onDelete={onDelete}
              onSettings={onSettings}
              onStartSession={onStartSession}
              onStopSession={onStopSession}
              onShare={onShare}
              onToggleEditGrant={onToggleEditGrant}
              onChatModeChange={onChatModeChange}
              shareCount={agentShareCounts[agent.id] || 0}
              isActionLoading={actionLoadingId === agent.id}
              currentUserId={currentUserId}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================
// Named Exports
// ============================================

export {
  AgentList,
  AgentRow,
  AgentAvatar,
  StatusBadge,
  OperationalStatusBadge,
  SessionInfo,
  ActionButton,
  SearchInput,
  FilterDropdown,
  EmptyState,
  LoadingSkeleton,
  ErrorState,
  AGENT_STATUS,
  OPERATIONAL_STATUS,
  CHAT_MODE,
  STATUS_COLORS,
  STATUS_LABELS,
  OPERATIONAL_STATUS_COLORS,
  OPERATIONAL_STATUS_LABELS,
  STATUS_FILTER_OPTIONS,
  SORT_OPTIONS,
  formatDate,
  formatDuration
};
